<template>
  <b-modal
    v-model="isVisible"
    :title="$t('backoffice.moderation.feedback-modal.title')"
    centered
    @hidden="handleModalHidden"
  >
    <b-form>
      <!-- AI Recommendation -->
      <div class="mb-2">
        <h6>{{ $t('backoffice.moderation.feedback-modal.ai-recommendation') }}</h6>
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="moderationAction === 'accept' ? 'CheckCircleIcon' : 'AlertCircleIcon'"
            :class="moderationAction === 'accept' ? 'text-success' : 'text-danger'"
            size="18"
            class="mr-1"
          />
          <span :class="moderationAction === 'accept' ? 'text-success' : 'text-danger'">
            {{ $t(`backoffice.moderation.feedback-modal.${moderationAction}-recommendation`) }}
          </span>
        </div>
      </div>

      <!-- Feedback for the User -->
      <b-form-group
        :label="$t('backoffice.moderation.feedback-modal.user-feedback-label')"
      >
        <b-form-textarea
          v-model="moderationFeedback.feedbackForUser"
          rows="3"
          :placeholder="$t('backoffice.moderation.feedback-modal.user-feedback-placeholder')"
        />
      </b-form-group>

      <!-- Agreement with AI Recommendation -->
      <b-form-group class="mb-2">
        <b-form-radio-group
          v-model="moderationFeedback.agreesWithAI"
          :options="[
            { text: $t('backoffice.moderation.feedback-modal.agree-with-ai'), value: true },
            { text: $t('backoffice.moderation.feedback-modal.disagree-with-ai'), value: false }
          ]"
        />
      </b-form-group>

      <!-- Feedback for the AI agent (only shown when disagreeing) -->
      <b-form-group
        v-if="moderationFeedback.agreesWithAI === false"
        :label="$t('backoffice.moderation.feedback-modal.ai-feedback-label')"
      >
        <b-form-textarea
          v-model="moderationFeedback.aiFeedback"
          rows="3"
          :placeholder="$t('backoffice.moderation.feedback-modal.ai-feedback-placeholder')"
        />
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        @click="handleCancel"
      >
        {{ $t('backoffice.moderation.feedback-modal.cancel') }}
      </b-button>
      <b-button
        :variant="moderationAction === 'accept' ? 'success' : 'danger'"
        @click="handleSubmit"
      >
        {{ $t(`backoffice.moderation.feedback-modal.${moderationAction}`) }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';

export default {
  name: 'ModerationModal',
  
  mixins: [ToastNotificationsMixin],

  props: {
    value: {
      type: Boolean,
      default: false
    },
    moderationAction: {
      type: String,
      required: true,
      validator: value => ['accept', 'reject'].includes(value)
    },
    content: {
      type: Object,
      required: true
    },
    itemType: {
      type: String,
      required: true
    },
    morphType: {
      type: String,
      required: true
    },
    itemTypeName: {
      type: String,
      required: true
    },
    parentKey: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      moderationFeedback: {
        feedbackForUser: '',
        aiFeedback: '',
        agreesWithAI: null
      }
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },

  watch: {
    value(newValue) {
      if (newValue) {
        this.resetFeedback();
      }
    },
    moderationAction(newAction) {
      if (newAction) {
        this.resetFeedback();
      }
    }
  },

  methods: {
    resetFeedback() {
      this.moderationFeedback = {
        feedbackForUser: '',
        aiFeedback: '',
        agreesWithAI: null
      };
      
      // Set default feedback messages
      if (this.moderationAction === 'accept') {
        this.moderationFeedback.feedbackForUser = this.$t('backoffice.moderation.feedback-modal.default-accept-user');
      } else {
        this.moderationFeedback.feedbackForUser = this.$t('backoffice.moderation.feedback-modal.default-reject-user');
      }
    },

    handleModalHidden() {
      this.$emit('modal-hidden');
    },

    handleCancel() {
      this.isVisible = false;
    },

    async handleSubmit() {
      try {
        // Create moderation check object
        const moderationCheck = {
          isAccepted: this.moderationFeedback.agreesWithAI,
          improveComment: this.moderationFeedback.aiFeedback
        };

        // Prepare request config
        const requestConfig = {
          [`${this.itemTypeName}Key`]: this.content.key,
          isDraft: false,
          moderationStatus: this.moderationAction === 'accept' ? 1 : 2,
          feedbackForUser: this.moderationFeedback.feedbackForUser,
          moderationCheck: moderationCheck
        };

        // Add parent key if provided
        if (this.parentKey) {
          requestConfig.parentKey = this.parentKey;
        }
        console.log('requestConfig', requestConfig);
        console.log('About to dispatch editItem with:', {
          itemType: this.itemType,
          morphType: this.morphType,
          requestConfig
        });

        // Update the content with moderation data
        const response = await this.$store.dispatch('editItem', {
          item: {
            itemType: this.itemType,
            morphType: this.morphType,
            requestConfig
          },
        });
        console.log('editItem dispatch completed, response:', response);

        // Check if the response indicates success
        // The store action can return either:
        // 1. response.data (direct data) - success case
        // 2. response (full response object) - success case  
        // 3. error.response (error object) - error case
        const isSuccess = response && 
          (response.status === 200 || response.status === undefined) && 
          !response.data?.error;
        
        if (isSuccess) {
          // Emit event to parent for handling success
          this.$emit('moderation-complete', {
            action: this.moderationAction,
            contentKey: this.content.key,
            response: response
          });

          // Show success notification
          this.notifySuccess(
            this.$t(`backoffice.moderation.success-${this.moderationAction}`)
          );

          // Close modal
          this.isVisible = false;
        } else {
          // Handle case where response indicates an error
          console.error('Moderation response indicates error:', response);
          this.notifyError(this.$t('error-message.general-error'));
        }
      } catch (error) {
        console.error('Error in handleSubmit:', error);
        this.notifyError(this.$t('error-message.general-error'));
      }
    }
  }
};
</script>
